import { PLLine, PLSectionKey, PLTransaction, ProfitAndLoss } from "@/interfaces/profitAndLoss.interface";

export const SECTION_ORDER: PLSectionKey[] = [
  "income",
  "cogs",
  "grossProfit",
  "expenses",
  "netOperatingIncome",
  "otherIncome",
  "otherExpenses",
  "netOtherIncome",
  "netIncome",
];

/** Sections that are plain totals with no expandable line items. */
export const TOTAL_ONLY_SECTIONS = new Set<PLSectionKey>(["grossProfit", "netOperatingIncome", "netOtherIncome", "netIncome"]);

/** Sections whose total should render bold/emphasized, matching a standard P&L statement. */
export const EMPHASIZED_SECTIONS = new Set<PLSectionKey>(["grossProfit", "netOperatingIncome", "netIncome"]);

/**
 * Flattens the statement into CSV rows, mirroring exactly what the table renders — full line
 * hierarchy (indented, regardless of which rows are currently expanded on screen) and the same
 * "Total" + monthly columns, with the same blank monthly cells on section-total rows.
 */
export const buildProfitAndLossCsvRows = (pl: ProfitAndLoss, displayColumns: string[]): (string | number)[][] => {
  const rows: (string | number)[][] = [["Line Item", "Total", ...displayColumns]];
  const round = (n: number) => Number(n.toFixed(2));

  const pushLine = (line: PLLine, depth: number) => {
    const lineTotal = line.amounts.length > 0 ? line.amounts[line.amounts.length - 1]! : 0;
    rows.push([
      `${"  ".repeat(depth)}${line.name}`,
      round(lineTotal),
      ...displayColumns.map((_, i) => round(line.amounts[i] ?? 0)),
    ]);
    line.children?.forEach((child) => pushLine(child, depth + 1));
  };

  for (const key of SECTION_ORDER) {
    const section = pl.sections.find((s) => s.key === key);
    if (!section) continue;

    if (TOTAL_ONLY_SECTIONS.has(key)) {
      rows.push([section.label, round(section.total), ...displayColumns.map(() => "")]);
      continue;
    }

    rows.push([section.label]);
    if (section.lines.length === 0) {
      rows.push(["(No line items)"]);
    } else {
      section.lines.forEach((line) => pushLine(line, 1));
    }
    rows.push([`Total for ${section.label}`, round(section.total), ...displayColumns.map(() => "")]);
  }

  return rows;
};

export const formatCurrency = (amount: number): string => {
  const sign = amount < 0 ? "-" : "";
  return `${sign}${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Math.abs(amount))}`;
};

export interface MatrixCell {
  total: number;
  transactions: PLTransaction[];
}

export interface MatrixRow {
  label: string;
  cells: MatrixCell[];
  rowTotal: number;
}

export interface MonthMatrix {
  months: string[];
  rows: MatrixRow[];
}

/** Formats a transaction date to match QBO's "MMM YYYY" monthly column titles (e.g. "Jan 2026"). */
const monthLabelFromDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "Unknown";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

/**
 * Pivots a flat transaction list into a rows x months matrix aligned to the parent
 * statement's `columns` (rather than auto-discovering whichever months appear in the data),
 * so every row has a cell for every month — including zero-activity months.
 */
export const buildMonthMatrix = (
  transactions: PLTransaction[],
  columns: string[],
  groupBy: "subLine" | "vendor",
): MonthMatrix => {
  const months = columns.filter((c) => c.trim().toLowerCase() !== "total");

  const rowKey = (t: PLTransaction): string =>
    (groupBy === "subLine" ? t.subLine : t.counterparty)?.trim() || "Uncategorized";

  const rowMap = new Map<string, PLTransaction[]>();
  for (const txn of transactions) {
    const key = rowKey(txn);
    if (!rowMap.has(key)) rowMap.set(key, []);
    rowMap.get(key)!.push(txn);
  }

  const rows: MatrixRow[] = Array.from(rowMap.entries()).map(([label, txns]) => {
    const cells: MatrixCell[] = months.map((month) => {
      const inMonth = txns.filter((t) => monthLabelFromDate(t.date) === month);
      return { total: inMonth.reduce((s, t) => s + t.amount, 0), transactions: inMonth };
    });
    return { label, cells, rowTotal: txns.reduce((s, t) => s + t.amount, 0) };
  });

  rows.sort((a, b) => Math.abs(b.rowTotal) - Math.abs(a.rowTotal));

  return { months, rows };
};
